import { type ReactNode, Suspense, useState } from 'react';
import { NavLink, Navigate, Outlet, useLocation } from 'react-router';
import { AccessDenied } from '../../components/AccessDenied';
import { LoadingNotice } from '../../components/ErrorNotice';
import { useI18n } from '../../lib/i18n/runtime';
import type { MessageKey } from '../../lib/i18n/messages';
import { canAny, useMembership } from '../workspace/membership-context';
import {
  ADMIN_TAB_GROUPS,
  type AdminTabGroup,
  adminTab,
  hasAdminAccess,
  visibleAdminTabs,
} from './admin-tabs';

const GROUP_KEYS: Readonly<Record<AdminTabGroup, MessageKey>> = {
  people: 'adminGroupPeople',
  ownership: 'adminGroupOwnership',
  oversight: 'adminGroupOversight',
  settings: 'adminGroupSettings',
};

const TAB_KEYS: Readonly<Record<string, MessageKey>> = {
  members: 'adminMembers',
  invitations: 'adminInvitations',
  permissions: 'adminPermissions',
  founders: 'navFounders',
  equity: 'adminEquity',
  governance: 'adminGovernance',
  finance: 'navFinance',
  audit: 'adminAudit',
  ai: 'adminAi',
  execution: 'adminExecution',
  notifications: 'notifications',
  security: 'adminSecurity',
  system: 'adminSystem',
};

export function AdminLayout() {
  const { t } = useI18n();
  const membership = useMembership();
  const tabs = visibleAdminTabs(membership);
  const location = useLocation();
  // Below the wide breakpoint the section list is collapsible; choosing a section closes it.
  const [navOpenAt, setNavOpenAt] = useState<string | null>(null);
  const navOpen = navOpenAt === location.pathname;
  const currentTab = tabs.find((tab) =>
    new RegExp(`/admin/${tab.path}(/|$)`).test(location.pathname),
  );

  if (!hasAdminAccess(membership) || tabs.length === 0) {
    return <AccessDenied />;
  }

  return (
    <div className="stack">
      <h1>{t('administration')}</h1>
      <div className="admin-layout">
        <button
          type="button"
          className="admin-nav-toggle"
          aria-expanded={navOpen}
          aria-controls="admin-nav"
          onClick={() => {
            setNavOpenAt(navOpen ? null : location.pathname);
          }}
        >
          <span>
            {t('adminSections')}
            {currentTab !== undefined && (
              <>
                : <strong>{t(TAB_KEYS[currentTab.path] as MessageKey)}</strong>
              </>
            )}
          </span>
          <span aria-hidden="true">{navOpen ? '▴' : '▾'}</span>
        </button>
        <nav
          id="admin-nav"
          aria-label={t('adminSections')}
          className={navOpen ? 'admin-nav admin-nav--open' : 'admin-nav'}
        >
          {ADMIN_TAB_GROUPS.map((group) => {
            const groupTabs = tabs.filter((tab) => tab.group === group);
            if (groupTabs.length === 0) return null;
            return (
              <div key={group} className="admin-nav__group">
                <p id={`admin-nav-${group}`} className="admin-nav__group-title">
                  {t(GROUP_KEYS[group])}
                </p>
                <ul aria-labelledby={`admin-nav-${group}`}>
                  {groupTabs.map((tab) => (
                    <li key={tab.path}>
                      <NavLink to={tab.path}>{t(TAB_KEYS[tab.path] as MessageKey)}</NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </nav>
        <div className="admin-layout__content">
          <Suspense fallback={<LoadingNotice>{t('loadingSection')}</LoadingNotice>}>
            <Outlet />
          </Suspense>
        </div>
      </div>
    </div>
  );
}

export function AdminIndex() {
  const membership = useMembership();
  const first = visibleAdminTabs(membership)[0];
  return first === undefined ? <AccessDenied /> : <Navigate to={first.path} replace />;
}

/** Renders an admin section only when the member holds one of the tab's capabilities. */
export function AdminSection({
  path,
  children,
  bare = false,
}: {
  path: string;
  children: ReactNode;
  /** Skip the section heading when the content brings its own. */
  bare?: boolean;
}) {
  const { t } = useI18n();
  const membership = useMembership();
  const tab = adminTab(path);
  if (!hasAdminAccess(membership) || !canAny(membership, tab.capabilities)) {
    return <AccessDenied />;
  }
  if (bare) {
    return <>{children}</>;
  }
  return (
    <section aria-labelledby={`admin-${path}-heading`} className="stack">
      <h2 id={`admin-${path}-heading`}>{t(TAB_KEYS[path] as MessageKey)}</h2>
      {children}
    </section>
  );
}
